(function () {
  'use strict';

  /** Dieux GA4 event tracking. */

  const GA4_MEASUREMENT_ID = 'G-PTVTTX2R7P';
  const DEBUG = false;
  const DEDUPE_WINDOW_MS = 2000;
  const INITIAL_PAGE_LOCATION = window.location.href;
  const PAGE_VIEW_DESTINATION_WAIT_MS = 2000;
  const PAGE_VIEW_DESTINATION_POLL_MS = 50;
  let initialGooglePageViewDestinations = null;

  function isAnalyticsAllowed() {
    try {
      if (window.Shopify && window.Shopify.customerPrivacy) {
        if (typeof Shopify.customerPrivacy.analyticsProcessingAllowed === 'function') {
          return Shopify.customerPrivacy.analyticsProcessingAllowed();
        }
        if (typeof Shopify.customerPrivacy.currentVisitorConsent === 'function') {
          const c = Shopify.customerPrivacy.currentVisitorConsent();
          if (c && c.analytics !== undefined) {
            return c.analytics === 'yes' || c.analytics === true;
          }
        }
      }
    } catch (e) {}
    return true;
  }

  window.dataLayer = window.dataLayer || [];

  const QUEUE_POLL_MS = 100;
  const QUEUE_MAX_WAIT_MS = 10000;
  const eventQueue = [];
  let gtagMounted = false;
  let pollHandle = null;
  let pollDeadline = 0;

  function isConsentResolved() {
    return !!(window.Shopify && window.Shopify.customerPrivacy &&
              typeof window.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function');
  }

  function isGtagConfigured() {
    if (gtagMounted) return true;
    const dl = window.dataLayer;
    if (!dl || !dl.length) return false;
    for (let i = 0; i < dl.length; i++) {
      const e = dl[i];
      if (!e) continue;
      const cmd = e[0] || e['0'];
      const id  = e[1] || e['1'];
      if (cmd === 'config' && id === GA4_MEASUREMENT_ID) {
        gtagMounted = true;
        return true;
      }
    }
    return false;
  }

  function isReady() {
    return isConsentResolved() && isGtagConfigured();
  }

  // gtag.js ignores plain arrays; queued events must flush as Arguments-shaped
  // objects with numeric keys.
  function pushAsGtagArguments(argsArray) {
    (function () { window.dataLayer.push(arguments); }).apply(null, argsArray);
  }

  function flushQueue() {
    if (!isAnalyticsAllowed()) {
      eventQueue.length = 0;
      return;
    }

    while (eventQueue.length) {
      const args = eventQueue.shift();
      pushAsGtagArguments(args);
      if (DEBUG) console.log('[GA4] ⤴ flushed queued event:', args[1]);
    }
  }

  function startPolling() {
    if (pollHandle) return;
    pollDeadline = Date.now() + QUEUE_MAX_WAIT_MS;
    pollHandle = setInterval(function () {
      if (isReady()) {
        clearInterval(pollHandle);
        pollHandle = null;
        if (DEBUG) console.log('[GA4] gtag mounted + consent resolved; flushing ' + eventQueue.length + ' queued event(s)');
        flushQueue();
      } else if (Date.now() >= pollDeadline) {
        clearInterval(pollHandle);
        pollHandle = null;
        if (DEBUG) console.warn('[GA4] gtag/consent never became ready in ' + QUEUE_MAX_WAIT_MS + 'ms; dropping ' + eventQueue.length + ' queued event(s)');
        eventQueue.length = 0;
      }
    }, QUEUE_POLL_MS);
  }

  window.ga4Event = function () {
    if (isReady()) {
      window.dataLayer.push(arguments);
    } else {
      const a = new Array(arguments.length);
      for (let i = 0; i < arguments.length; i++) a[i] = arguments[i];
      eventQueue.push(a);
      if (DEBUG) console.log('[GA4] ⏸ queued (gtag not mounted yet):', a[1]);
      startPolling();
    }
  };

  const recentEvents = new Map();

  function getEventKey(eventName, params, toggleState) {
    return [
      eventName,
      params.custom_click_name || '',
      params.custom_button_text || '',
      params.custom_element_type || '',
      params.custom_click_id || '',
      params.custom_element_id || '',
      params.page_location || '',
      params.page_path || '',
      params.hero_title || '',
      params.hero_variant || '',
      params.custom_product_handle || '',
      params.custom_variant_id || '',
      params.productVariantId || '',
      params.productTitle || '',
      params.custom_search_term || '',
      params.custom_click_productid || '',
      params.custom_click_variantid || '',
      toggleState || ''
    ].join('|');
  }

  function isDuplicateEvent(eventName, params, toggleState) {
    const key = getEventKey(eventName, params, toggleState);
    const now = Date.now();
    for (const [k, t] of recentEvents.entries()) {
      if (now - t > DEDUPE_WINDOW_MS) recentEvents.delete(k);
    }
    if (recentEvents.has(key) && (now - recentEvents.get(key)) < DEDUPE_WINDOW_MS) {
      return true;
    }
    recentEvents.set(key, now);
    return false;
  }

  function cleanParams(params) {
    const clean = {};
    const stringLimits = {
      page_location: 1000,
      page_path: 1000,
      page_referrer: 420,
      page_title: 300
    };
    for (const [k, v] of Object.entries(params || {})) {
      if (v === null || v === undefined) continue;
      const key = String(k).replace(/[^a-zA-Z0-9_]/g, '_').substring(0, 40);
      if (typeof v === 'string') {
        if (k === 'custom_button_text') {
          const c = cleanText(v);
          if (c !== null) clean[key] = c;
        } else {
          const t = v.trim();
          if (t.length > 0) clean[key] = t.substring(0, stringLimits[k] || 100);
        }
      } else if (typeof v === 'number' || typeof v === 'boolean') {
        clean[key] = String(v);
      }
    }
    return clean;
  }

  function sendToGA4(eventName, params, toggleState, options) {
    if (!isAnalyticsAllowed()) {
      if (DEBUG) console.log('[GA4] Dropped (no consent):', eventName);
      return;
    }

    if (typeof window.ga4Event !== 'function') {
      if (DEBUG) console.error('[GA4] ga4Event not ready:', eventName);
      return;
    }

    const clean = cleanParams(params);
    if (options && options.keepNumbers) {
      for (const [k, v] of Object.entries(params || {})) {
        if (typeof v === 'number' && Number.isFinite(v)) {
          clean[String(k).replace(/[^a-zA-Z0-9_]/g, '_').substring(0, 40)] = v;
        }
      }
    }
    if (isDuplicateEvent(eventName, clean, toggleState)) return;

    // Keep supplemental theme events out of Ads and Merchant Center.
    clean.send_to = GA4_MEASUREMENT_ID;

    window.ga4Event('event', eventName, clean);
    if (DEBUG) console.log('[GA4] ✓ Event sent:', eventName, clean);
  }

  function cloneGooglePageViewDestinations(destinations) {
    if (Array.isArray(destinations)) {
      const cleanDestinations = destinations.filter((destination) => (
        typeof destination === 'string' && destination.trim().length > 0
      ));
      if (cleanDestinations.includes(GA4_MEASUREMENT_ID)) return cleanDestinations.slice();
    } else if (
      typeof destinations === 'string' &&
      destinations.trim().length > 0 &&
      destinations.includes(GA4_MEASUREMENT_ID)
    ) {
      return destinations;
    }

    return null;
  }

  function isInitialPageLocation(pageLocation) {
    if (!pageLocation) return false;

    try {
      const initialUrl = new URL(INITIAL_PAGE_LOCATION);
      const candidateUrl = new URL(pageLocation, initialUrl);
      return (
        candidateUrl.origin === initialUrl.origin &&
        candidateUrl.pathname === initialUrl.pathname &&
        candidateUrl.search === initialUrl.search
      );
    } catch (error) {
      return false;
    }
  }

  function findInitialGooglePageViewDestinations() {
    if (initialGooglePageViewDestinations) {
      return cloneGooglePageViewDestinations(initialGooglePageViewDestinations);
    }

    const dataLayer = window.dataLayer;
    if (!Array.isArray(dataLayer)) return null;

    for (let i = 0; i < dataLayer.length; i++) {
      const entry = dataLayer[i];
      if (!entry) continue;

      const command = entry[0] || entry['0'];
      const eventName = entry[1] || entry['1'];
      const params = entry[2] || entry['2'];
      if (command !== 'event' || eventName !== 'page_view' || !params) continue;
      if (!isInitialPageLocation(params.page_location)) continue;

      const destinations = cloneGooglePageViewDestinations(params.send_to);
      if (destinations) {
        initialGooglePageViewDestinations = destinations;
        return cloneGooglePageViewDestinations(initialGooglePageViewDestinations);
      }
    }

    return null;
  }

  function pushVirtualPageView(clean, queuedAt) {
    if (!isAnalyticsAllowed()) {
      if (DEBUG) console.log('[GA4] Dropped virtual page view (no consent)');
      return;
    }

    const destinations = findInitialGooglePageViewDestinations();
    if (!destinations && Date.now() - queuedAt < PAGE_VIEW_DESTINATION_WAIT_MS) {
      window.setTimeout(
        () => pushVirtualPageView(clean, queuedAt),
        PAGE_VIEW_DESTINATION_POLL_MS
      );
      return;
    }

    if (typeof window.ga4Event !== 'function') {
      if (DEBUG) console.error('[GA4] ga4Event not ready: page_view');
      return;
    }

    if (destinations) clean.send_to = destinations;

    try {
      window.ga4Event('event', 'page_view', clean);
      if (DEBUG) console.log('[GA4] ✓ Virtual page view sent:', clean);
    } catch (error) {
      if (DEBUG) console.error('[GA4] Virtual page view failed:', error);
    }
  }

  function sendVirtualPageView(params) {
    const clean = cleanParams(params);
    pushVirtualPageView(clean, Date.now());
  }

  const CHANNEL_ITEM_ID = /^(shopify_[A-Z]{2}_)\d+_\d+$/;
  const CHANNEL_INTERNAL_PARAMS = new Set(['event_id', 'ext_client_id', 'developer_id', 'user_data', 'shopify_event_name']);
  const CHANNEL_EVENT_SETTLE_MS = 500;
  const CHANNEL_PRODUCT_OWNER_SELECTOR = '#MainContent product-component[data-template-product-state-owner][data-ga4-item]';
  const CHANNEL_LIST_GRID_SELECTOR = '#MainContent collection-component [data-collection-grid]';
  const CHANNEL_LIST_ITEM_KEYS = ['id', 'name', 'brand', 'category', 'price', 'variant', 'index', 'sku', 'variant_id'];
  const synthesizedChannelParams = new WeakSet();

  function isMarketingAllowed() {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      return !!(privacy && typeof privacy.marketingAllowed === 'function' && privacy.marketingAllowed() === true);
    } catch (error) {
      return false;
    }
  }

  function findChannelEvent(eventName, fromIndex) {
    const dataLayer = window.dataLayer;
    if (!Array.isArray(dataLayer)) return null;

    for (let i = fromIndex; i < dataLayer.length; i++) {
      const entry = dataLayer[i];
      if (!entry || (entry[0] || entry['0']) !== 'event' || (entry[1] || entry['1']) !== eventName) continue;

      const params = entry[2] || entry['2'];
      if (!params || synthesizedChannelParams.has(params)) continue;
      if (!Array.isArray(params.items) || !params.items[0]) continue;

      const match = CHANNEL_ITEM_ID.exec(String(params.items[0].id || ''));
      if (match && cloneGooglePageViewDestinations(params.send_to)) return { params: params, prefix: match[1] };
    }

    return null;
  }

  function readChannelItem(el, prefix) {
    let data;
    let product;
    try {
      data = JSON.parse(el.getAttribute('data-ga4-item'));
      product = JSON.parse(el.getAttribute('view-event-payload')).product;
    } catch (error) {
      return null;
    }

    const variantId = data && (data.variant_id || el.getAttribute('data-variant-id'));
    if (!product || !product.id || !variantId || typeof data.price !== 'number') return null;

    return {
      id: prefix + product.id + '_' + variantId,
      name: product.title,
      brand: data.brand ?? null,
      category: data.category ?? null,
      price: data.price,
      variant: data.variant ?? null,
      sku: data.sku ?? null,
      variant_id: String(variantId)
    };
  }

  function shapeChannelItem(template, values) {
    const item = {};
    Object.keys(template).forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(values, key)) item[key] = values[key];
    });
    return item;
  }

  function readViewItemValues(initial) {
    const owner = document.querySelector(CHANNEL_PRODUCT_OWNER_SELECTOR);
    const source = owner && readChannelItem(owner, initial.prefix);
    if (!source) return null;

    const item = shapeChannelItem(initial.params.items[0], Object.assign({ quantity: 1 }, source));
    return { items: [item], ecomm_prodid: [item.id], ecomm_totalvalue: source.price, value: source.price };
  }

  function readViewItemListValues(initial) {
    const grid = document.querySelector(CHANNEL_LIST_GRID_SELECTOR);
    const owner = grid && grid.closest('collection-component');
    let collection;
    try {
      collection = JSON.parse(owner.getAttribute('view-event-payload')).collection;
    } catch (error) {
      return null;
    }
    if (!collection || !collection.id) return null;

    const items = [];
    grid.querySelectorAll('[data-collection-card] product-card[data-ga4-item]').forEach((card) => {
      if (card.closest('[data-collection-card]').hidden) return;
      const source = readChannelItem(card, initial.prefix);
      if (source) items.push(shapeChannelItem(initial.params.items[0], Object.assign({ index: items.length }, source)));
    });
    if (!items.length) return null;

    return {
      items: items,
      item_list_id: String(collection.id),
      item_list_name: owner.getAttribute('data-active-title') || ''
    };
  }

  function cloneChannelParams(initial, values) {
    const marketing = isMarketingAllowed();
    const params = {};
    Object.keys(initial.params).forEach((key) => {
      const value = initial.params[key];
      if (key === 'send_to') {
        if (marketing) params.send_to = Array.isArray(value) ? value.slice() : value;
        else params.send_to = Array.isArray(value) ? [GA4_MEASUREMENT_ID] : GA4_MEASUREMENT_ID;
      } else if (Object.prototype.hasOwnProperty.call(values, key)) {
        params[key] = values[key];
      } else if (!CHANNEL_INTERNAL_PARAMS.has(key) && ['string', 'number', 'boolean'].includes(typeof value)) {
        params[key] = value;
      }
    });
    return params;
  }

  function getFallbackListEvent() {
    const country = window.Shopify && window.Shopify.country;
    if (typeof country !== 'string' || !/^[A-Z]{2}$/.test(country)) return null;
    if (!findInitialGooglePageViewDestinations()) return null;

    const item = {};
    CHANNEL_LIST_ITEM_KEYS.forEach((key) => { item[key] = null; });
    return {
      params: { send_to: GA4_MEASUREMENT_ID, item_list_id: '', item_list_name: '', items: [item] },
      prefix: 'shopify_' + country + '_'
    };
  }

  const pendingChannelSends = new Set();

  function flushPendingChannelSends() {
    pendingChannelSends.forEach((send) => send());
  }

  function synthesizeChannelEvent(template) {
    const base = String(template || '').split('.')[0];
    const eventName = base === 'product' ? 'view_item' : base === 'collection' ? 'view_item_list' : '';
    if (!eventName || !isAnalyticsAllowed()) return;

    const initial = findChannelEvent(eventName, 0) ||
      (eventName === 'view_item_list' ? getFallbackListEvent() : null);
    if (!initial) return;

    const values = eventName === 'view_item' ? readViewItemValues(initial) : readViewItemListValues(initial);
    if (!values) return;

    const navigationIndex = window.dataLayer.length;
    let timer = 0;
    const send = () => {
      window.clearTimeout(timer);
      if (!pendingChannelSends.delete(send)) return;
      if (!isAnalyticsAllowed() || typeof window.ga4Event !== 'function') return;
      if (findChannelEvent(eventName, navigationIndex)) return;

      const params = cloneChannelParams(initial, values);
      synthesizedChannelParams.add(params);
      window.ga4Event('event', eventName, params);
      if (DEBUG) console.log('[GA4] ✓ Channel event synthesized:', eventName, params);
    };
    pendingChannelSends.add(send);
    timer = window.setTimeout(send, CHANNEL_EVENT_SETTLE_MS);
  }

  function handleVirtualPageView(event) {
    if (!event || !event.detail || event.detail.dieuxVirtualPageView !== true) return;

    const page = event.detail.page || {};
    let pageUrl;
    try {
      pageUrl = new URL(page.url || window.location.href, window.location.origin);
    } catch (error) {
      return;
    }
    if (pageUrl.origin !== window.location.origin) return;

    const params = {
      page_location: pageUrl.href.substring(0, 1000),
      page_title: String(page.title || document.title).trim().substring(0, 300)
    };

    if (event.detail.pageReferrer) {
      try {
        const referrer = new URL(event.detail.pageReferrer, window.location.origin);
        if (
          referrer.origin === pageUrl.origin &&
          referrer.pathname === pageUrl.pathname &&
          referrer.searchParams.get('page') === pageUrl.searchParams.get('page')
        ) {
          return;
        }
        if (referrer.origin === window.location.origin) {
          params.page_referrer = referrer.href.substring(0, 420);
        }
      } catch (error) {}
    }

    try {
      sendVirtualPageView(params);
    } catch (error) {
      if (DEBUG) console.error('[GA4] Virtual page view failed:', error);
    }

    try {
      synthesizeChannelEvent(page.template);
    } catch (error) {
      if (DEBUG) console.error('[GA4] Channel event synthesis failed:', error);
    }
  }

  function getDataAttribute(el, attrName) {
    if (!el) return null;
    const camel = attrName.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    let n = el;
    while (n && n !== document.body) {
      if (n.dataset && n.dataset[camel]) return n.dataset[camel];
      const v = n.getAttribute && n.getAttribute('data-' + attrName);
      if (v !== null && v !== undefined && v !== '') return v;
      n = n.parentElement;
    }
    return null;
  }

  const getClickName = (el) =>
    getDataAttribute(el, 'click-widget') || getDataAttribute(el, 'click-name');
  const getClickId        = (el) => getDataAttribute(el, 'click-id');
  const getClickLinkName  = (el) => getDataAttribute(el, 'click-link-name');
  const getClickProduct   = (el) => getDataAttribute(el, 'click-product');
  const getClickVariant   = (el) => getDataAttribute(el, 'click-variant');
  const getClickProductId = (el) => getDataAttribute(el, 'click-productid');
  const getClickVariantId = (el) => getDataAttribute(el, 'click-variantid');

  const SECTION_INSTANCE_PREFIX = /^(?:template|sections)--\d+__/;

  function stripSectionInstancePrefix(id) {
    return id.replace(/(?:template|sections)--\d+__/g, '');
  }

  const GENERATED_ID = /^dieux-standalone-link/;

  function getElementId(el) {
    let n = el;
    while (n && n !== document.body) {
      if (n.id && !(typeof n.id === 'string' && GENERATED_ID.test(n.id))) {
        return typeof n.id === 'string' ? stripSectionInstancePrefix(n.id) : null;
      }
      n = n.parentElement;
    }
    return null;
  }

  function getProductHandle(url) {
    if (!url) return null;
    const m = url.match(/\/products\/([^?#/]+)/);
    return m ? m[1] : null;
  }

  function getVariantIdFromUrl(url) {
    if (!url) return null;
    try { return new URL(url, window.location.origin).searchParams.get('variant'); }
    catch (e) { return null; }
  }

  const TEMPLATE_MAIN_ALIASES = new Set(['product', 'collection', 'list-collections', 'search', 'blog', 'article']);
  const GROUP_SECTION_ALIASES = new Set(['header', 'footer', 'cart-template', 'announcement-bar']);

  function getTemplateBase() {
    const main = document.getElementById('MainContent');
    const template = main && main.getAttribute('data-template');
    return template ? template.split('.')[0] : '';
  }

  function getLegacySectionAlias(key, isTemplateSection) {
    if (key === 'header_section') return 'header';
    if (key === 'cart_drawer') return 'cart-template';
    if (key.indexOf('header_announcements') === 0) return 'announcement-bar';
    if (!isTemplateSection) return null;
    if (key === 'sticky_add_to_cart') return getTemplateBase() === 'product' ? 'product-template' : null;
    if (key === 'main' || key === 'collection-grid') {
      const base = getTemplateBase();
      if (key === 'collection-grid' && base !== 'list-collections' && base !== 'collection') return null;
      return TEMPLATE_MAIN_ALIASES.has(base) ? base + '-template' : null;
    }
    if (GROUP_SECTION_ALIASES.has(key)) {
      const base = getTemplateBase();
      return base ? base + '-' + key : null;
    }
    return null;
  }

  function getSectionInfo(el) {
    const s = el.closest && el.closest('.shopify-section');
    if (!s || !s.id) return { sectionId: null, sectionHandle: null };
    const rawId = s.id.replace(/^shopify-section-/, '');
    const id = rawId.replace(SECTION_INSTANCE_PREFIX, '');
    const alias = getLegacySectionAlias(id, rawId.indexOf('template--') === 0);
    if (alias) return { sectionId: alias, sectionHandle: alias };
    const m = id.match(/__([^_]+)$/);
    return { sectionId: id, sectionHandle: m ? m[1] : id };
  }

  function cleanText(text) {
    if (!text) return null;
    const div = document.createElement('div');
    div.innerHTML = text;
    div.querySelectorAll('script, style').forEach((el) => el.remove());
    let c = (div.textContent || div.innerText || '')
      .replace(/[\r\n\t\f\v]+/g, ' ')
      .replace(/ /g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (c.includes('{') || c.match(/^\s*--[\w-]+\s*:/)) return null;
    if (c.length > 40) c = c.substring(0, 40) + '...';
    return c || null;
  }

  const VISIBLE_TEXT_NODE_BUDGET = 50;
  const TEXT_SNAPSHOT_NODE_LIMIT = 100;
  const HIDDEN_TEXT_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'S', 'STRIKE', 'DEL']);

  function isRenderedText(el) {
    let style;
    try { style = window.getComputedStyle(el); } catch (e) { return true; }
    if (style.display === 'none') return false;
    if (style.visibility === 'hidden' || style.visibility === 'collapse') return false;
    const deco = style.textDecorationLine || style.textDecoration || '';
    return deco.indexOf('line-through') === -1;
  }

  function getVisibleText(root) {
    if (!root || root.nodeType !== 1) return '';
    let out = '';
    let visited = 0;
    let exceeded = false;
    function walk(node) {
      if (!node || exceeded) return;
      if (node.nodeType === 3) { out += node.nodeValue; return; }
      if (node.nodeType !== 1) return;
      if (HIDDEN_TEXT_TAGS.has(node.tagName)) return;
      if (node !== root && node.getAttribute && node.getAttribute('aria-hidden') === 'true') return;
      visited += 1;
      if (visited > VISIBLE_TEXT_NODE_BUDGET) { exceeded = true; return; }
      if (!isRenderedText(node)) return;
      const kids = node.childNodes;
      for (let i = 0; i < kids.length; i++) walk(kids[i]);
    }
    walk(root);
    return exceeded ? null : out;
  }

  function getSnapshotVisibleText(root, nodes) {
    const shown = new Map();
    let styleReads = 0;

    function isShown(el) {
      if (shown.has(el)) return shown.get(el);
      let result = !HIDDEN_TEXT_TAGS.has(el.tagName) &&
        (el === root || el.getAttribute('aria-hidden') !== 'true');
      if (result && el !== root) {
        const parent = el.parentElement;
        result = parent ? isShown(parent) : !el.isConnected;
      }
      if (result && el.isConnected) {
        styleReads += 1;
        result = styleReads <= VISIBLE_TEXT_NODE_BUDGET && isRenderedText(el);
      }
      shown.set(el, result);
      return result;
    }

    let out = '';
    for (let i = 0; i < nodes.length; i++) {
      const parent = nodes[i][0];
      if (!parent || (parent.isConnected && !root.contains(parent))) continue;
      if (isShown(parent)) out += nodes[i][1];
      if (styleReads > VISIBLE_TEXT_NODE_BUDGET) return null;
    }
    return out;
  }

  function snapshotTextNodes(root) {
    if (!root || root.nodeType !== 1) return null;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      if (nodes.length === TEXT_SNAPSHOT_NODE_LIMIT) return null;
      nodes.push([node.parentElement, node.nodeValue]);
    }
    return nodes;
  }

  function getTextRoot(el) {
    if (el.type !== 'radio' && el.type !== 'checkbox') return el;
    let lbl = null;
    if (el.id) {
      try { lbl = document.querySelector('label[for="' + CSS.escape(el.id) + '"]'); } catch (e) {}
    }
    return lbl || (el.closest && el.closest('label')) || null;
  }

  function getLabelAttribute(el) {
    return el.getAttribute ? (el.getAttribute('aria-label') || el.getAttribute('title')) : null;
  }

  function hasAlternateText(nodes) {
    if (!nodes) return false;
    let first = '';
    for (let i = 0; i < nodes.length; i++) {
      const text = nodes[i][1].trim();
      if (!text) continue;
      if (!first) first = text;
      else if (text !== first) return true;
    }
    return false;
  }

  function captureClickText(target, toggleState) {
    const root = getTextRoot(target);
    const snapshot = {
      root: root,
      nodes: snapshotTextNodes(root),
      label: getLabelAttribute(target),
      linkName: target.getAttribute ? target.getAttribute('data-click-link-name') : null
    };
    if (toggleState && hasAlternateText(snapshot.nodes)) {
      snapshot.visibleText = getSnapshotVisibleText(root, snapshot.nodes);
    }
    return snapshot;
  }

  function readVisibleText(root, snapshot) {
    if (snapshot && snapshot.visibleText !== undefined) return snapshot.visibleText;
    return snapshot && snapshot.nodes ? getSnapshotVisibleText(root, snapshot.nodes) : getVisibleText(root);
  }

  function readTextContent(root, snapshot) {
    if (!snapshot || !snapshot.nodes) return root.textContent;
    return snapshot.nodes.map((entry) => entry[1]).join('');
  }

  function getElementText(el, snapshot) {
    let raw = null;
    if (el.tagName === 'SELECT') {
      const opt = el.options[el.selectedIndex];
      raw = opt ? opt.text : null;
    } else if (el.type === 'radio' || el.type === 'checkbox') {
      const src = snapshot ? snapshot.root : getTextRoot(el);
      if (src) {
        raw = readVisibleText(src, snapshot);
        if (raw === null) raw = readTextContent(src, snapshot);
      }
    } else {
      raw = readVisibleText(el, snapshot);
      const label = snapshot ? snapshot.label : getLabelAttribute(el);
      if (raw === null) raw = label || readTextContent(el, snapshot);
      else if (!raw.trim()) raw = label;
      if (!raw || !raw.trim()) {
        raw = snapshot ? snapshot.linkName : (el.getAttribute && el.getAttribute('data-click-link-name'));
      }
    }
    return cleanText(raw);
  }

  function isNativeChoice(el) {
    return el.tagName === 'INPUT' && (el.type === 'checkbox' || el.type === 'radio');
  }

  function getToggleState(el) {
    if (!el.getAttribute) return '';
    const states = [
      el.getAttribute('aria-expanded'),
      el.getAttribute('aria-pressed'),
      el.getAttribute('aria-checked')
    ];
    if (el.tagName === 'SUMMARY' && el.parentElement && el.parentElement.tagName === 'DETAILS') {
      states.push(el.parentElement.open ? 'open' : 'closed');
    }
    if (isNativeChoice(el)) states.push(el.checked ? 'checked' : 'unchecked');
    return states.some((state) => state !== null) ? states.join(',') : '';
  }

  const PENDING_CLICK_TEXT = Object.freeze({ pendingClickText: true });

  function buildClickContext(targetEl, originalEl) {
    const href = targetEl.getAttribute ? targetEl.getAttribute('href') : null;
    const linkUrl = href && !href.startsWith('#') ? href : null;
    const productHandle = getProductHandle(linkUrl);
    const variantIdUrl = getVariantIdFromUrl(linkUrl);
    const { sectionId, sectionHandle } = getSectionInfo(targetEl);

    const ctx = {
      custom_click_name: getClickName(targetEl) || getClickName(originalEl),
      custom_button_text: PENDING_CLICK_TEXT,
      custom_element_type: targetEl.tagName.toLowerCase(),
      custom_element_role: targetEl.getAttribute ? targetEl.getAttribute('role') : null,
      page_path: window.location.pathname,
      page_title: document.title
    };

    const clickId        = getClickId(targetEl) || getClickId(originalEl);
    const clickLinkName  = getClickLinkName(targetEl) || getClickLinkName(originalEl);
    const clickProduct   = getClickProduct(targetEl) || getClickProduct(originalEl);
    const clickVariant   = getClickVariant(targetEl) || getClickVariant(originalEl);
    const clickProductId = getClickProductId(targetEl) || getClickProductId(originalEl);
    const clickVariantId = getClickVariantId(targetEl) || getClickVariantId(originalEl);
    const elementId      = getElementId(targetEl);

    if (clickId != null)        ctx.custom_click_id = clickId;
    if (clickLinkName != null)  ctx.custom_click_link_name = clickLinkName;
    if (clickProduct != null)   ctx.custom_click_product = clickProduct;
    if (clickVariant != null)   ctx.custom_click_variant = clickVariant;
    if (clickProductId != null) ctx.custom_click_productid = clickProductId;
    if (clickVariantId != null) ctx.custom_click_variantid = clickVariantId;
    if (elementId != null)      ctx.custom_element_id = elementId;
    if (linkUrl != null)        ctx.custom_link_url = linkUrl;
    if (productHandle != null)  ctx.custom_product_handle = productHandle;
    if (variantIdUrl != null)   ctx.custom_variant_id = variantIdUrl;
    if (sectionId != null)      ctx.custom_section_id = sectionId;
    if (sectionHandle != null)  ctx.custom_section_handle = sectionHandle;
    return ctx;
  }

  const CLICKABLE_SELECTOR = [
    'button',
    'a',
    'input[type="submit"]',
    'input[type="radio"]',
    'input[type="checkbox"]',
    'select',
    'summary',
    '[role="button"]',
    '[role="link"]',
    '[role="checkbox"]',
    '[role="radio"]',
    '[role="switch"]',
    '[role="tab"]',
    '[role="menuitem"]',
    '[role="menuitemcheckbox"]',
    '[role="menuitemradio"]',
    '[role="option"]',
    '[role="treeitem"]',
    '[aria-pressed]',
    '[aria-expanded]',
    '[aria-selected]'
  ].join(', ');

  const pendingClickSends = [];
  let pendingLeadingSends = 0;

  function flushPendingClickSends() {
    while (pendingClickSends.length) {
      const send = pendingClickSends.shift();
      if (pendingLeadingSends) pendingLeadingSends -= 1;
      try {
        send();
      } catch (error) {
        if (DEBUG) console.error('[GA4] Click send failed:', error);
      }
    }
  }

  function queueSend(send, leading) {
    if (leading) {
      pendingClickSends.splice(pendingLeadingSends, 0, send);
      pendingLeadingSends += 1;
    } else {
      pendingClickSends.push(send);
    }
    window.setTimeout(flushPendingClickSends, 0);
  }

  function sendClickEvent(eventName, params, capture) {
    queueSend(function () {
      if (params.custom_button_text === PENDING_CLICK_TEXT) {
        params.custom_button_text = capture && capture.textEl
          ? getElementText(capture.textEl, capture.text)
          : null;
      }
      sendToGA4(eventName, params, capture ? capture.toggleState : '');
    });
  }

  function captureClick(target) {
    const toggleState = getToggleState(target);
    return {
      textEl: target,
      text: captureClickText(target, toggleState && !isNativeChoice(target)),
      toggleState: toggleState
    };
  }

  const PRESS_REPLAY_WINDOW_MS = 10000;
  const ACTIVATION_KEYS = new Set(['Enter', ' ', 'Spacebar']);
  const NON_TEXT_INPUT_TYPES = new Set(['button', 'checkbox', 'color', 'file', 'image', 'radio', 'reset', 'submit']);
  let unclickedPress = null;
  let unclickedKeyPress = null;

  function rememberPress(event) {
    if (event.isTrusted && event.target instanceof Element) {
      unclickedPress = { target: event.target, time: Date.now() };
    }
  }

  function isTextEntry(el) {
    if (el.isContentEditable || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true;
    return el.tagName === 'INPUT' && !NON_TEXT_INPUT_TYPES.has(el.type);
  }

  function rememberKeyPress(event) {
    if (!event.isTrusted || event.repeat || event.isComposing || !ACTIVATION_KEYS.has(event.key)) return;
    const origin = event.composedPath()[0];
    if (!(event.target instanceof Element) || isTextEntry(origin instanceof Element ? origin : event.target)) return;
    unclickedKeyPress = { target: event.target, time: Date.now() };
  }

  function releaseKeyPress(event) {
    if (!event.isTrusted || !ACTIVATION_KEYS.has(event.key) || !unclickedKeyPress) return;
    const press = unclickedKeyPress;
    window.setTimeout(() => {
      if (unclickedKeyPress === press) unclickedKeyPress = null;
    }, 0);
  }

  function isPressFor(press, event) {
    return !!press &&
      Date.now() - press.time <= PRESS_REPLAY_WINDOW_MS &&
      event.target instanceof Element &&
      event.target.contains(press.target);
  }

  function isUserClick(event) {
    if (event.isTrusted) {
      unclickedPress = null;
      unclickedKeyPress = null;
      return true;
    }
    if (isPressFor(unclickedPress, event)) {
      unclickedPress = null;
      return true;
    }
    if (isPressFor(unclickedKeyPress, event)) {
      unclickedKeyPress = null;
      return true;
    }
    return false;
  }

  function isForwardedLabelClick(originalEl) {
    const lbl = originalEl.closest && originalEl.closest('label');
    return !!(lbl && lbl.control && lbl.contains(lbl.control) && lbl.control !== originalEl);
  }

  function handleClick(event) {
    if (!isUserClick(event)) return;
    trackClick(event);
  }

  function handleAnalyticsClick(event) {
    if (!(event.target instanceof Element)) return;
    if (unclickedPress && event.target.contains(unclickedPress.target)) unclickedPress = null;
    trackClick(event);
  }

  function trackClick(event) {
    const originalEl = event.target;
    const clickNameEl = originalEl.closest && originalEl.closest('[data-click-name]');
    const subscriptionWidget = originalEl.closest && originalEl.closest('.subscription-widget, .js-subscription-option');

    let target = originalEl.closest && originalEl.closest(CLICKABLE_SELECTOR);
    if (!target && (clickNameEl || subscriptionWidget) && isForwardedLabelClick(originalEl)) return;
    if (!target && clickNameEl) target = clickNameEl;
    if (!target && subscriptionWidget) {
      target = originalEl.closest('.subscription-option, .subscription-frequency, .subscription-frequency__button') || originalEl;
    }
    if (!target) return;

    const ctx = buildClickContext(target, originalEl);

    const productIdEl = getDataAttribute(target, 'product-id') || getDataAttribute(originalEl, 'product-id');
    if (productIdEl && (target.tagName === 'SELECT' || target.type === 'radio')) {
      const sv = getSelectedVariantForProduct(target, productIdEl);
      if (sv) {
        ctx.custom_click_variant = cleanText(sv.title);
        ctx.custom_click_variantid = sv.id;
      }
    }

    if (target.tagName === 'SELECT') return;

    const capture = captureClick(target);

    const ga4ClickEl = originalEl.closest && originalEl.closest('[data-ga4-click]');
    if (ga4ClickEl) return fireCustomGa4Event(ga4ClickEl, ctx, capture);

    const heroEl = originalEl.closest && originalEl.closest('[data-ga4-hero]');
    if (heroEl && (target.tagName === 'A' || target.tagName === 'BUTTON' || (target.classList && target.classList.contains('btn')))) {
      return fireHeroClick(heroEl, ctx, target, capture);
    }

    const navAbEl = originalEl.closest && originalEl.closest('[data-ga4-nav-ab="1"]');
    const megaLinkEl = originalEl.closest && originalEl.closest('[data-mega-link]');
    const touchMegaToggleEl = megaLinkEl && (
      event.pointerType === 'touch' || megaLinkEl.dataset.megaTouchToggle === 'true'
    );
    if (navAbEl && !touchMegaToggleEl && (target.tagName === 'A' || target.tagName === 'BUTTON')) {
      return fireNavClick(navAbEl, ctx, target, capture);
    }

    if (touchMegaToggleEl) {
      ctx.custom_click_name = 'Main Menu - Dropdown Toggle';
      delete ctx.custom_link_url;
      delete ctx.custom_product_handle;
      delete ctx.custom_variant_id;
      return fireElementClick(ctx, capture);
    }

    const atcEl = originalEl.closest && originalEl.closest('.js-add-to-cart, .js-add-to-cart-alias, .js-add-to-cart-variant');
    if (atcEl) return fireAddToCart(atcEl, ctx, capture);

    fireElementClick(ctx, capture);
  }

  function fireElementClick(ctx, capture) {
    sendClickEvent('element_click', ctx, capture);
  }

  function fireCustomGa4Event(ga4ClickEl, ctx, capture) {
    const eventName = ga4ClickEl.getAttribute('data-ga4-click');
    let declaredParams = {};
    const pAttr = ga4ClickEl.getAttribute('data-ga4-params');
    if (pAttr) {
      try { declaredParams = JSON.parse(pAttr) || {}; } catch (e) {}
    }
    const payload = Object.assign({}, ctx, declaredParams);
    sendClickEvent(eventName, payload, capture);
  }

  const __heroAbGroups = {};

  function removeHeroAbCandidate(heroEl) {
    const heroSlide = heroEl.closest && heroEl.closest('[data-hero-slide]');
    if (heroSlide && heroSlide.contains(heroEl)) {
      heroSlide.remove();
      return;
    }
    const slideEl = heroEl.closest && heroEl.closest('.swiper-slide');
    if (slideEl && slideEl.contains(heroEl)) {
      slideEl.remove();
      return;
    }
    heroEl.remove();
  }

  function chooseHeroAbIndex(count) {
    if (count <= 1) return 0;

    if (window.crypto && window.crypto.getRandomValues) {
      const values = new Uint32Array(1);
      const max = Math.floor(0x100000000 / count) * count;
      do {
        window.crypto.getRandomValues(values);
      } while (values[0] >= max);
      return values[0] % count;
    }

    return Math.floor(Math.random() * count);
  }

  function fireHeroClick(heroEl, ctx, clickedTarget, capture) {
    const heroTitle = heroEl.getAttribute('data-ga4-hero-title') || '';
    const abEnabled = heroEl.getAttribute('data-ga4-hero-ab') === '1';
    const abVariant = heroEl.getAttribute('data-ga4-hero-variant') || '';
    const params = Object.assign({}, ctx, {
      hero_title: heroTitle
    });
    if (abEnabled && abVariant) params.hero_variant = abVariant;
    if (clickedTarget && clickedTarget.href) params.custom_link_url = clickedTarget.href;
    sendClickEvent('hero_click', params, capture);
  }

  function fireHeroViewed(heroEl) {
    if (heroEl.dataset.ga4HeroViewed === '1') return;
    heroEl.dataset.ga4HeroViewed = '1';

    const heroTitle = heroEl.getAttribute('data-ga4-hero-title') || '';
    const abEnabled = heroEl.getAttribute('data-ga4-hero-ab') === '1';
    const abVariant = heroEl.getAttribute('data-ga4-hero-variant') || '';
    const params = {
      page_path: window.location.pathname,
      page_title: document.title,
      hero_title: heroTitle
    };
    if (abEnabled && abVariant) params.hero_variant = abVariant;
    sendToGA4('hero_viewed', params);
  }

  function initHeroTracking() {
    const heroes = document.querySelectorAll('[data-ga4-hero="1"]');
    heroes.forEach(function (heroEl) {
      const heroTitle = heroEl.getAttribute('data-ga4-hero-title') || '';
      const abEnabled = heroEl.getAttribute('data-ga4-hero-ab') === '1';
      const abVariant = heroEl.getAttribute('data-ga4-hero-variant') || '';
      const designMode = heroEl.getAttribute('data-ga4-design-mode') === '1';
      const managedSlideshow = heroEl.closest('[data-hero-ab-managed="true"]');

      if (managedSlideshow) {
        if (designMode) return;
        if (abEnabled && heroEl.getAttribute('data-hero-ab-selected') !== 'true') return;
        if (heroEl.getAttribute('data-current') === 'true') queueHeroViewed(heroEl);
        return;
      }

      if (!abEnabled || !heroTitle || designMode) {
        queueHeroViewed(heroEl);
        return;
      }

      const cookieKey = 'ab_hero_' + heroTitle;
      const m = document.cookie.match(new RegExp('(?:^|; )' + cookieKey + '=([^;]*)'));
      const cookieVariant = m ? decodeURIComponent(m[1]) : '';

      if (!__heroAbGroups[heroTitle]) __heroAbGroups[heroTitle] = { blocks: [], timer: null };
      const group = __heroAbGroups[heroTitle];
      group.blocks.push({ el: heroEl, variant: abVariant, cookieVariant: cookieVariant, cookieKey: cookieKey });

      clearTimeout(group.timer);
      group.timer = setTimeout(function () {
        let chosen = -1;
        const latestMatch = document.cookie.match(new RegExp('(?:^|; )' + cookieKey + '=([^;]*)'));
        const storedVariant = latestMatch
          ? decodeURIComponent(latestMatch[1])
          : (group.blocks[0] && group.blocks[0].cookieVariant);
        if (storedVariant) {
          chosen = group.blocks.findIndex(function (block) {
            return block.variant === storedVariant;
          });
        }
        if (chosen < 0) {
          chosen = chooseHeroAbIndex(group.blocks.length);
          document.cookie = cookieKey + '=' + encodeURIComponent(group.blocks[chosen].variant) +
                            '; path=/; max-age=' + (60 * 60 * 24 * 30);
        }

        for (let i = 0; i < group.blocks.length; i++) {
          if (i === chosen) queueHeroViewed(group.blocks[i].el);
          else removeHeroAbCandidate(group.blocks[i].el);
        }
      }, 0);
    });
  }

  function queueHeroViewed(heroEl) {
    if (heroEl.dataset.ga4HeroViewed === '1' || heroEl.dataset.ga4HeroViewQueued === '1') return;
    heroEl.dataset.ga4HeroViewQueued = '1';

    if (document.readyState === 'complete') {
      delete heroEl.dataset.ga4HeroViewQueued;
      fireHeroViewed(heroEl);
    } else {
      window.addEventListener('load', function () {
        delete heroEl.dataset.ga4HeroViewQueued;
        if (!heroEl.isConnected) return;
        if (
          heroEl.closest('[data-hero-ab-managed="true"]') &&
          heroEl.getAttribute('data-current') !== 'true'
        ) {
          return;
        }
        fireHeroViewed(heroEl);
      }, { once: true });
    }
  }

  function handleManagedHeroViewed(event) {
    const heroEl = event.detail && event.detail.hero;
    if (!(heroEl instanceof Element) || heroEl.getAttribute('data-ga4-hero') !== '1') return;
    if (heroEl.getAttribute('data-ga4-design-mode') === '1') return;
    if (
      heroEl.getAttribute('data-ga4-hero-ab') === '1' &&
      heroEl.getAttribute('data-hero-ab-selected') !== 'true'
    ) {
      return;
    }
    fireHeroViewed(heroEl);
  }

  function flushNavViewedQueue() {
    var queue = window.__navAbViewedQueue;
    if (!queue || !queue.length) return;
    while (queue.length) {
      var rec = queue.shift();
      sendToGA4('nav_viewed', {
        nav_title: rec.nav_title,
        nav_variant: rec.nav_variant,
        match_count: rec.match_count,
        page_path: window.location.pathname,
        page_title: document.title
      });
    }
  }

  function fireNavClick(navEl, ctx, clickedTarget, capture) {
    var navTitle = navEl.getAttribute('data-ga4-nav-title') || '';
    var navVariant = navEl.getAttribute('data-ga4-nav-variant') || '';
    var params = Object.assign({}, ctx, {
      nav_title: navTitle,
      nav_variant: navVariant
    });
    if (clickedTarget && clickedTarget.href) params.custom_link_url = clickedTarget.href;
    sendClickEvent('nav_click', params, capture);
  }

  function fireAddToCart(atcEl, ctx, capture) {
    const wrapper = atcEl.closest('.js-product-wrapper') || atcEl;
    const productTitle = wrapper.getAttribute('data-title') || ctx.custom_click_product || '';
    const productVariantId = wrapper.getAttribute('data-variant') || ctx.custom_click_variantid || '';
    const productPrice = wrapper.getAttribute('data-price') || '';
    const quantity = atcEl.closest('form')?.elements?.quantity?.value;
    const productQty = Math.max(1, Number.parseInt(quantity || '1', 10) || 1);

    sendClickEvent('addToCart', Object.assign({}, ctx, {
      productTitle: productTitle,
      productVariantId: productVariantId,
      productPrice: productPrice,
      productQty: productQty
    }), capture);
  }

  function observeDataLayer() {
    // no-op
  }

  function handleUpsellAdded(event) {
    const d = event.detail || {};
    sendToGA4('upsell_add_to_cart', {
      custom_click_name: 'Cart - Upsell Added',
      custom_click_product: d.productTitle || '',
      custom_click_productid: d.productId || '',
      custom_click_variant: d.variantTitle || '',
      custom_click_variantid: d.variantId || '',
      custom_element_type: 'upsell',
      page_path: window.location.pathname,
      page_title: document.title
    });
  }

  function handleCrossSellAdded(event) {
    const d = event.detail || {};
    sendToGA4('cross_sell_add_to_cart', {
      custom_click_name: 'PDP - Cross Sell Added',
      custom_click_product: d.productTitle || '',
      custom_click_productid: d.productId || '',
      custom_click_variant: d.variantTitle || '',
      custom_click_variantid: d.variantId || '',
      custom_element_type: 'cross-sell',
      custom_source: d.source || 'PDP Cross Sell',
      page_path: window.location.pathname,
      page_title: document.title
    });
  }

  let searchOpenDispatched = false;

  function dispatchSearchOpen(trigger) {
    if (searchOpenDispatched) return;
    searchOpenDispatched = true;
    document.dispatchEvent(new CustomEvent('search:open', { detail: { trigger: trigger || 'unknown' } }));
  }

  function dispatchSearchClose(term, extra) {
    if (!searchOpenDispatched) return;
    searchOpenDispatched = false;
    const detail = Object.assign({ searchTerm: term || '' }, extra);
    document.dispatchEvent(new CustomEvent('search:close', { detail: detail }));
  }

  function isSiteSearchInput(input) {
    if (input.getAttribute('name') === 'q') return true;
    if (input.type !== 'search' || !input.form) return false;
    return (input.form.getAttribute('action') || '').includes('/search');
  }

  function wireSearchAutoDetection() {
    // `toggle` does not bubble, but the capture path still reaches document, so
    // search wrappers rendered after load need no re-scan.
    document.addEventListener('toggle', function (event) {
      const det = event.target;
      if (!det || det.tagName !== 'DETAILS') return;
      const input = det.querySelector('input[type="search"], input[name="q"]');
      if (!input) return;
      if (det.open) {
        dispatchSearchOpen('header-icon');
      } else {
        dispatchSearchClose(input.value ? input.value.trim() : '');
      }
    }, true);

    document.addEventListener('click', function (event) {
      const target = event.target && event.target.closest && event.target.closest('[data-click-name]');
      if (!target) return;
      const name = target.getAttribute('data-click-name') || '';
      if (/search.*(toggle|open)/i.test(name)) {
        dispatchSearchOpen(name);
      } else if (/search.*close/i.test(name)) {
        const region = target.closest('dieux-search');
        if (region) region.dispatchEvent(new CustomEvent('search:flush', { bubbles: false }));
        const input = (region && region.querySelector('input[name="q"]')) || document.querySelector('input[name="q"]');
        const results = region && region.querySelector('[data-search-results]');
        const productCount = results ? Number.parseInt(results.dataset.searchResultCount || '0', 10) || 0 : 0;
        dispatchSearchClose(input && input.value ? input.value.trim() : '', {
          hadResults: productCount > 0,
          productCount: productCount,
          articleCount: 0
        });
      }
    }, true);

    document.addEventListener('focusin', function (event) {
      const t = event.target;
      if (!t || t.tagName !== 'INPUT') return;
      if (isSiteSearchInput(t)) dispatchSearchOpen('input-focus');
    }, true);

    document.addEventListener('submit', function (event) {
      const form = event.target;
      if (!form || form.tagName !== 'FORM') return;
      const action = form.getAttribute('action') || '';
      if (!action.includes('/search')) return;
      const input = form.querySelector('input[type="search"], input[name="q"]');
      const term = input && input.value ? input.value.trim() : '';
      if (term) document.dispatchEvent(new CustomEvent('search:query', { detail: { searchTerm: term } }));
    }, true);
  }

  function handleSearchOpen(event) {
    const d = event.detail || {};
    sendToGA4('search_open', {
      custom_search_trigger: d.trigger || 'unknown',
      page_path: window.location.pathname,
      page_title: document.title
    });
  }

  function handleSearchClose(event) {
    const d = event.detail || {};
    const params = {
      custom_search_term: d.searchTerm || '',
      custom_had_results: d.hadResults ? 'true' : 'false',
      custom_product_count: d.productCount || 0,
      custom_article_count: d.articleCount || 0,
      page_path: window.location.pathname,
      page_title: document.title
    };
    queueSend(() => sendToGA4('search_close', params), true);
  }

  function handleSearchQuery(event) {
    const d = event.detail || {};
    const term = d.searchTerm || '';
    sendToGA4('search_query', {
      custom_search_term: term,
      custom_search_term_length: term.length,
      page_path: window.location.pathname,
      page_title: document.title
    });
  }

  function handleSearchResults(event) {
    const d = event.detail || {};
    const productCount = d.productCount || 0;
    const articleCount = d.articleCount || 0;
    const total = productCount + articleCount;
    const pagePath = window.location.pathname;
    const pageTitle = document.title;
    queueSend(() => {
      sendToGA4('search_results_loaded', {
        custom_search_term: d.searchTerm || '',
        custom_product_count: productCount,
        custom_article_count: articleCount,
        custom_total_results: total,
        custom_has_results: total > 0 ? 'true' : 'false',
        page_path: pagePath,
        page_title: pageTitle
      });
      if (total === 0 && d.searchTerm) {
        sendToGA4('search_no_results', {
          custom_search_term: d.searchTerm,
          custom_search_term_length: d.searchTerm.length,
          page_path: pagePath,
          page_title: pageTitle
        });
      }
    }, true);
  }

  function handleSearchResultClick(event) {
    const d = event.detail || {};
    const p = {
      custom_search_term: d.searchTerm || '',
      custom_result_type: d.resultType || 'unknown',
      custom_result_title: d.resultTitle || '',
      custom_result_position: d.resultPosition || 0,
      custom_result_id: d.resultId || '',
      custom_result_url: d.resultUrl || '',
      page_path: window.location.pathname,
      page_title: document.title
    };
    if (d.resultVariant) p.custom_result_variant = d.resultVariant;
    if (d.resultVariantId) p.custom_result_variant_id = d.resultVariantId;
    sendToGA4('search_result_click', p);
  }

  function getSelectedVariantForProduct(el, productId) {
    if (!productId) return null;
    const script = document.querySelector(`script[data-product-variants="${productId}"]`);
    if (!script) return null;
    let data;
    try { data = JSON.parse(script.textContent); } catch (e) { return null; }

    const container = el.closest('.pdp-cross-sell__variant-dropdowns, [data-variant-dropdowns]');
    if (!container) return null;

    const opts = [];
    container.querySelectorAll('.pdp-cross-sell__swatch-group, [data-swatch-group]').forEach((g) => {
      const i = parseInt(g.getAttribute('data-option-index'));
      const checked = g.querySelector('input:checked');
      if (checked && !isNaN(i)) opts[i] = checked.value;
    });
    container.querySelectorAll('select[data-option-index]').forEach((s) => {
      const i = parseInt(s.getAttribute('data-option-index'));
      if (!isNaN(i)) opts[i] = s.value;
    });

    return data.find((v) => (
      (!opts[0] || v.option1 === opts[0]) &&
      (!opts[1] || v.option2 === opts[1]) &&
      (!opts[2] || v.option3 === opts[2])
    )) || null;
  }

  const ENGAGEMENT_EVENT_NAME = /^[a-z][a-z0-9_]{0,39}$/;

  function handleEngagementEvent(event) {
    const detail = event.detail || {};
    if (typeof detail.name !== 'string' || !ENGAGEMENT_EVENT_NAME.test(detail.name)) return;
    sendToGA4(detail.name, detail.params || {}, String(detail.key || ''), { keepNumbers: true });
  }

  const OPTION_CLICK_ATTRIBUTES = ['data-click-product', 'data-click-productid', 'data-click-variant', 'data-click-variantid'];

  function handleChange(event) {
    const el = event.target;

    if (el.tagName === 'SELECT') {
      const ctx = buildClickContext(el, el);

      const opt = el.options[el.selectedIndex];
      if (opt) {
        const clickIdentity = OPTION_CLICK_ATTRIBUTES.some((name) => opt.hasAttribute(name));
        const pt = clickIdentity ? opt.getAttribute('data-click-product') : opt.getAttribute('data-product-title');
        const pi = clickIdentity ? opt.getAttribute('data-click-productid') : opt.getAttribute('data-product-id');
        const vt = clickIdentity
          ? opt.getAttribute('data-click-variant')
          : opt.getAttribute('data-variant-title') || opt.getAttribute('data-display-label');
        const vi = clickIdentity ? opt.getAttribute('data-click-variantid') : opt.getAttribute('data-variant-id');
        if (pt) ctx.custom_click_product = pt;
        if (pi) ctx.custom_click_productid = pi;
        if (vt) ctx.custom_click_variant = cleanText(vt);
        if (vi) ctx.custom_click_variantid = vi;
      }

      const productId = getDataAttribute(el, 'product-id');
      if (productId) {
        const sv = getSelectedVariantForProduct(el, productId);
        if (sv) {
          ctx.custom_click_variant = cleanText(sv.title);
          ctx.custom_click_variantid = sv.id;
        }
      }

      if (ctx.custom_button_text === PENDING_CLICK_TEXT) ctx.custom_button_text = getElementText(el);
      sendToGA4('element_click', ctx);
      return;
    }

    if (el.type === 'radio' || el.type === 'checkbox') {
      handleClick(event);
    }
  }

  function init() {
    observeDataLayer();
    document.addEventListener('dieux:hero-viewed', handleManagedHeroViewed, true);
    document.addEventListener('dieux:virtual-page-view', handleVirtualPageView, true);
    initHeroTracking();
    flushNavViewedQueue();
    const navQueue = window.__navAbViewedQueue = window.__navAbViewedQueue || [];
    navQueue.push = function () {
      const length = Array.prototype.push.apply(navQueue, arguments);
      flushNavViewedQueue();
      return length;
    };
    wireSearchAutoDetection();

    document.addEventListener('pointerup', rememberPress, { capture: true, passive: true });
    document.addEventListener('keydown', rememberKeyPress, { capture: true, passive: true });
    document.addEventListener('keyup', releaseKeyPress, { capture: true, passive: true });
    document.addEventListener('click', handleClick, true);
    document.addEventListener('dieux:analytics-click', handleAnalyticsClick, true);
    document.addEventListener('dieux:analytics-event', handleEngagementEvent, true);
    document.addEventListener('change', handleChange, true);
    window.addEventListener('pagehide', () => {
      flushPendingChannelSends();
      flushPendingClickSends();
    });

    document.addEventListener('upsell:added', handleUpsellAdded, true);
    document.addEventListener('cross-sell:added', handleCrossSellAdded, true);

    document.addEventListener('search:open', handleSearchOpen, true);
    document.addEventListener('search:close', handleSearchClose, true);
    document.addEventListener('search:query', handleSearchQuery, true);
    document.addEventListener('search:results', handleSearchResults, true);
    document.addEventListener('search:result_click', handleSearchResultClick, true);

    if (DEBUG) console.log('[GA4] google-events.js initialized (piggybacking on window.dataLayer; G&Y app owns gtag config)');
  }

  function start() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  if (document.prerendering) {
    document.addEventListener('prerenderingchange', start, { once: true });
  } else {
    start();
  }
})();
